import { useState, useEffect, useCallback, useRef } from 'react';
import { apiRequest, getImageUrl } from '../utils/api';
import confetti from 'canvas-confetti';

// CSS animations - simplified for better performance
const animationStyles = `
@keyframes slideInUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes selected-pulse {
    0%, 100% { 
        box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.5), 0 0 20px rgba(139, 92, 246, 0.3);
    }
    50% { 
        box-shadow: 0 0 0 4px rgba(139, 92, 246, 1), 0 0 30px rgba(139, 92, 246, 0.5);
    }
}

@keyframes winner-pulse {
    0%, 100% { 
        box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.5), 0 0 20px rgba(251, 191, 36, 0.3);
    }
    50% { 
        box-shadow: 0 0 0 4px rgba(251, 191, 36, 1), 0 0 30px rgba(251, 191, 36, 0.5);
    }
}

.vote-card {
    animation: slideInUp 0.4s ease-out forwards;
    opacity: 1;
}

.vote-card:nth-child(1) { animation-delay: 0.05s; }
.vote-card:nth-child(2) { animation-delay: 0.1s; }
.vote-card:nth-child(3) { animation-delay: 0.15s; }
.vote-card:nth-child(4) { animation-delay: 0.2s; }
.vote-card:nth-child(5) { animation-delay: 0.25s; }
.vote-card:nth-child(6) { animation-delay: 0.3s; }

.selected-card {
    opacity: 1 !important;
    animation: selected-pulse 1.5s ease-in-out infinite !important;
    border-color: #8B5CF6 !important;
    background: linear-gradient(to bottom, rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.2)) !important;
    transform: scale(1.02);
}

.winner-card {
    opacity: 1 !important;
    animation: winner-pulse 2s ease-in-out infinite !important;
}
`;

const VotingModal = ({ isOpen, onClose, vote, organizationId, onVoteComplete }) => {
    const [selectedOption, setSelectedOption] = useState(null);
    const [hasVoted, setHasVoted] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [results, setResults] = useState([]);
    const [totalVotes, setTotalVotes] = useState(0);
    const [winner, setWinner] = useState(null);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [loading, setLoading] = useState(false);
    const [phase, setPhase] = useState('voting'); // voting, results, winner
    const [showCelebration, setShowCelebration] = useState(false);
    const pollIntervalRef = useRef(null);
    const countdownRef = useRef(null);

    // Initialize state from vote data
    useEffect(() => {
        if (vote) {
            setHasVoted(vote.has_voted || false);
            if (vote.user_vote_option_id) {
                setSelectedOption(vote.user_vote_option_id);
            }
            if (vote.has_voted && vote.show_live_results) {
                setShowResults(true);
                fetchResults();
            }
            
            // Calculate remaining time
            if (vote.remaining_time) {
                setTimeRemaining(vote.remaining_time);
            }
            
            // Set phase based on vote status
            if (['completed', 'campaign_active'].includes(vote.status)) {
                setPhase('winner');
                fetchResults();
            } else if (vote.has_voted) {
                setPhase('results');
            }
        }
    }, [vote]);

    // Countdown timer
    useEffect(() => {
        if (timeRemaining > 0 && phase === 'voting') {
            countdownRef.current = setInterval(() => {
                setTimeRemaining(prev => {
                    if (prev <= 1000) {
                        // Time's up - fetch final results
                        fetchResults();
                        setPhase('winner');
                        return 0;
                    }
                    return prev - 1000;
                });
            }, 1000);
        }

        return () => {
            if (countdownRef.current) clearInterval(countdownRef.current);
        };
    }, [timeRemaining, phase]);

    // Poll for results if voted and showing results
    useEffect(() => {
        if (showResults && vote?.show_live_results && phase === 'results') {
            pollIntervalRef.current = setInterval(fetchResults, 3000);
        }

        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        };
    }, [showResults, phase]);

    const fetchResults = async () => {
        try {
            const response = await apiRequest(`/votes/public/${vote.id}/results`);
            if (response.success) {
                setResults(response.data.results || []);
                setTotalVotes(response.data.total_votes || 0);
                setWinner(response.data.winner);
                
                // Check if we should show celebration
                if (['completed', 'campaign_active'].includes(response.data.phase) && !showCelebration) {
                    setShowCelebration(true);
                    setPhase('winner');
                    triggerCelebration();
                }
            }
        } catch (error) {
            console.error('Fetch results error:', error);
        }
    };

    const triggerCelebration = () => {
        // Multiple confetti bursts
        const count = 200;
        const defaults = {
            origin: { y: 0.7 },
            zIndex: 10000
        };

        function fire(particleRatio, opts) {
            confetti({
                ...defaults,
                ...opts,
                particleCount: Math.floor(count * particleRatio)
            });
        }

        fire(0.25, { spread: 26, startVelocity: 55 });
        fire(0.2, { spread: 60 });
        fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
        fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
        fire(0.1, { spread: 120, startVelocity: 45 });

        // Additional celebration after delay
        setTimeout(() => {
            confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.6 },
                zIndex: 10000
            });
        }, 500);
    };

    const handleVote = async (optionId) => {
        if (hasVoted || loading) return;

        // Visual feedback
        setSelectedOption(optionId);
        setLoading(true);

        try {
            const response = await apiRequest(`/votes/public/${vote.id}/cast`, {
                method: 'POST',
                body: JSON.stringify({ vote_option_id: optionId })
            });

            if (response.success) {
                setHasVoted(true);
                setResults(response.data.results || []);
                setTotalVotes(response.data.total_votes || 0);
                
                // Transition to results
                setTimeout(() => {
                    setShowResults(true);
                    setPhase('results');
                }, 500);

                // Mini celebration for voting
                confetti({
                    particleCount: 30,
                    spread: 50,
                    origin: { y: 0.8 },
                    zIndex: 10000,
                    colors: ['#8B5CF6', '#EC4899', '#F59E0B']
                });

                if (onVoteComplete) onVoteComplete();
            }
        } catch (error) {
            console.error('Vote error:', error);
            setSelectedOption(null);
        } finally {
            setLoading(false);
        }
    };

    const formatTime = useCallback((ms) => {
        if (ms <= 0) return '00:00';
        
        const totalSeconds = Math.floor(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        if (hours > 0) {
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }, []);

    const getOptionPercentage = (optionId) => {
        const option = results.find(r => r.id === optionId);
        return option?.vote_percentage || 0;
    };

    const getOptionVoteCount = (optionId) => {
        const option = results.find(r => r.id === optionId);
        return option?.vote_count || 0;
    };

    if (!isOpen || !vote) return null;

    return (
        <>
            <style>{animationStyles}</style>
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                {/* Simple gradient background - no heavy animations */}
                <div 
                    className="absolute inset-0 bg-gradient-to-br from-purple-900 via-gray-900 to-pink-900"
                    onClick={onClose}
                />
                
                {/* Simple overlay */}
                <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

                {/* Modal Content */}
                <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    {/* Close Button */}
                    <button 
                        onClick={onClose}
                        className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-all"
                    >
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Header */}
                    <div className="text-center mb-8 px-4 pt-6">
                        <div className="inline-block px-4 py-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-white text-sm font-medium mb-4">
                            🗳️ OYLAMA
                        </div>
                        <h1 className="text-3xl md:text-4xl font-black text-white mb-2">
                            {vote.title}
                        </h1>
                        {vote.description && (
                            <p className="text-white/70 text-sm">{vote.description}</p>
                        )}

                        {/* Countdown Timer */}
                        {phase === 'voting' && timeRemaining > 0 && (
                            <div className="mt-6">
                                <div className="countdown-timer inline-flex items-center gap-3 px-6 py-3 bg-black/30 rounded-2xl backdrop-blur">
                                    <span className="text-white/60 text-sm">Kalan Süre</span>
                                    <span className={`font-mono text-2xl font-bold ${
                                        timeRemaining < 60000 ? 'text-red-400' : 'text-white'
                                    }`}>
                                        {formatTime(timeRemaining)}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Results Header - Oy sayısı gizli, sadece durum gösteriliyor */}
                        {showResults && (
                            <div className="mt-6 flex items-center justify-center gap-4">
                                <div className="px-4 py-2 bg-white/10 rounded-xl backdrop-blur">
                                    <span className="text-white/60 text-xs block">Durum</span>
                                    <span className="text-white text-sm font-medium">📊 Canlı Sonuçlar</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Winner Celebration - Simplified header */}
                    {phase === 'winner' && winner && (
                        <div className="text-center mb-6 px-4">
                            <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 rounded-2xl border border-amber-500/50">
                                <span className="text-4xl">🏆</span>
                                <div className="text-left">
                                    <h2 className="text-xl font-bold text-amber-400">
                                        {winner.product_name}
                                    </h2>
                                    <p className="text-white/70 text-sm">
                                        %{winner.vote_percentage} ile kazandı!
                                    </p>
                                </div>
                                {winner.discount_percentage && (
                                    <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                                        %{winner.discount_percentage} İNDİRİM
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Vote Options Grid - Always visible */}
                    <div className={`grid gap-4 px-4 pb-8 ${
                        vote.options?.length <= 4 ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-3'
                    }`}>
                        {vote.options?.map((option, idx) => {
                            const isSelected = selectedOption === option.id;
                            const percentage = getOptionPercentage(option.id);
                            const voteCount = getOptionVoteCount(option.id);
                            const isLeading = showResults && results[0]?.id === option.id;
                            const isWinner = phase === 'winner' && winner?.id === option.id;

                            return (
                                <div
                                    key={option.id}
                                    onClick={() => !hasVoted && phase === 'voting' && handleVote(option.id)}
                                    className={`vote-card relative overflow-hidden rounded-2xl transition-all duration-300 border-3 ${
                                        isWinner 
                                            ? 'border-amber-500 winner-card scale-105 z-10' 
                                            : isSelected 
                                                ? 'selected-card border-purple-500 scale-102 z-10' 
                                                : 'border-white/10 bg-white/5'
                                    } ${!hasVoted && phase === 'voting' ? 'cursor-pointer hover:border-white/30 hover:bg-white/10' : ''}`}
                                    style={{ 
                                        borderWidth: isSelected || isWinner ? '3px' : '2px',
                                        opacity: 1 
                                    }}
                                >
                                    {/* Progress bar (results) */}
                                    {showResults && (
                                        <div 
                                            className={`absolute bottom-0 left-0 right-0 transition-all duration-700 ${
                                                isWinner ? 'bg-amber-500/40' : 'bg-purple-500/30'
                                            }`}
                                            style={{ height: `${percentage}%` }}
                                        />
                                    )}

                                    {/* Content */}
                                    <div className="relative p-4">
                                        {/* Winner badge */}
                                        {isWinner && (
                                            <div className="absolute -top-1 -right-1 bg-gradient-to-r from-amber-400 to-yellow-500 text-black text-xs font-bold px-2 py-1 rounded-full z-10 shadow-lg">
                                                🏆 KAZANAN
                                            </div>
                                        )}
                                        
                                        {/* Leading badge */}
                                        {!isWinner && isLeading && showResults && (
                                            <div className="absolute -top-1 -right-1 bg-amber-500 text-black text-xs font-bold px-2 py-1 rounded-full z-10">
                                                👑 1.
                                            </div>
                                        )}

                                        {/* Product Image */}
                                        <div className={`aspect-square rounded-xl overflow-hidden bg-black/30 mb-3 ${
                                            isWinner ? 'ring-2 ring-amber-400' : ''
                                        }`}>
                                            {option.product_image ? (
                                                <img
                                                    src={getImageUrl(option.product_image)}
                                                    alt={option.product_name}
                                                    className={`w-full h-full object-cover transition-transform duration-300 ${
                                                        isSelected || isWinner ? 'scale-105' : ''
                                                    }`}
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-4xl text-white/30">
                                                    🍽️
                                                </div>
                                            )}
                                        </div>

                                        {/* Product Name */}
                                        <h3 className={`font-bold text-center truncate ${
                                            isWinner ? 'text-amber-400' : 'text-white'
                                        }`}>
                                            {option.product_name}
                                        </h3>

                                        {/* Vote Percentage - Sadece yüzde ve grafik, oy sayısı yok */}
                                        {showResults && (
                                            <div className="mt-3">
                                                {/* Progress Bar */}
                                                <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-1">
                                                    <div 
                                                        className={`h-full rounded-full transition-all duration-1000 ${
                                                            isWinner ? 'bg-gradient-to-r from-amber-400 to-yellow-500' : 'bg-gradient-to-r from-purple-500 to-pink-500'
                                                        }`}
                                                        style={{ width: `${percentage}%` }}
                                                    />
                                                </div>
                                                {/* Percentage Only */}
                                                <div className={`text-center font-mono text-lg font-bold ${
                                                    isWinner ? 'text-amber-400' : 'text-white'
                                                }`}>
                                                    {percentage}%
                                                </div>
                                            </div>
                                        )}

                                        {/* Selection indicator - always show if selected */}
                                        {isSelected && (
                                            <>
                                                {/* Large checkmark badge */}
                                                <div className="absolute top-2 left-2 w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-xl z-20 ring-2 ring-white">
                                                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                                {/* "Seçildi" label */}
                                                <div className="absolute bottom-2 left-2 right-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold py-1.5 px-3 rounded-full text-center shadow-lg z-20">
                                                    ✓ SEÇİLDİ
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Loading state */}
                    {loading && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                            <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
                        </div>
                    )}

                    {/* Already voted message */}
                    {hasVoted && !showResults && (
                        <div className="text-center px-4 pb-8">
                            <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-4 inline-block">
                                <span className="text-green-400">✓ Oyunuz kaydedildi!</span>
                            </div>
                            {vote.show_live_results && (
                                <button
                                    onClick={() => {
                                        setShowResults(true);
                                        setPhase('results');
                                        fetchResults();
                                    }}
                                    className="mt-4 block mx-auto px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition"
                                >
                                    📊 Sonuçları Gör
                                </button>
                            )}
                        </div>
                    )}

                    {/* Skip voting button */}
                    {!hasVoted && phase === 'voting' && (
                        <div className="text-center px-4 pb-8">
                            <button
                                onClick={onClose}
                                className="text-white/50 hover:text-white/70 text-sm underline transition"
                            >
                                Oy kullanmadan devam et →
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default VotingModal;

